/**
 * XeroPageKpis — page-level 4-up KPI strip aggregating COGS + SIT recency.
 * Silence is the reward when clean: the "Needs attention" tile only grows an
 * amber left-border when there is something to act on, and shows an emerald 0
 * otherwise. "Last push" goes amber past 24h.
 *
 * Tiles read from the already-fetched COGS/SIT stats so the page makes no extra
 * round-trips; the posted dollar amount is summed from the activity feed the
 * Overview tab already loads and passed down.
 */
import type { RouterOutputs } from '@/lib/trpc';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import { formatRelative, hoursSince } from '../lib/format';

type CogsStats = RouterOutputs['xero']['getCogsStats'];
type SitStats = RouterOutputs['xero']['getSitStats'];

function KpiTile({
  eyebrow,
  value,
  foot,
  accent = 'neutral',
}: {
  eyebrow: string;
  value: string;
  foot?: string;
  accent?: 'neutral' | 'amber' | 'emerald';
}) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card px-4 py-3',
        accent === 'amber' && 'border-l-4 border-l-amber-500',
        accent === 'emerald' && 'border-l-4 border-l-emerald-500'
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {eyebrow}
      </p>
      <p
        className={cn(
          'mt-0.5 text-xl font-semibold tabular-nums',
          accent === 'amber' && 'text-amber-700',
          accent === 'emerald' && 'text-emerald-700'
        )}
      >
        {value}
      </p>
      {foot && <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">{foot}</p>}
    </div>
  );
}

export function XeroPageKpis({
  cogsStats,
  sitStats,
  postedAmount,
}: {
  cogsStats: CogsStats | undefined;
  sitStats: SitStats | undefined;
  postedAmount: number | null;
}) {
  const { format } = useCurrencyFormatter();

  const pendingCogs = cogsStats?.pendingCount ?? 0;
  const pendingSit = sitStats?.pendingCount ?? 0;
  const awaiting = pendingCogs + pendingSit;

  const failed = (cogsStats?.totalFailed ?? 0) + (sitStats?.failedCount ?? 0);
  const postedJournals = (cogsStats?.totalPushed ?? 0) + (sitStats?.succeededCount ?? 0);

  // Most-recent successful push across both pipelines.
  const lastPushAt = [cogsStats?.lastPushAt, sitStats?.lastPushAt]
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null;
  const lastPushHours = hoursSince(lastPushAt);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <KpiTile
        eyebrow="Awaiting Xero"
        value={String(awaiting)}
        foot={`${pendingCogs} COGS · ${pendingSit} reclass`}
      />
      <KpiTile
        eyebrow="Needs attention"
        value={String(failed)}
        foot={failed > 0 ? 'Failed journals to retry' : 'All clear'}
        accent={failed > 0 ? 'amber' : 'emerald'}
      />
      <KpiTile
        eyebrow="Posted to Xero"
        value={postedAmount !== null ? format(postedAmount, 'AUD') : '-'}
        foot={`Across ${postedJournals} journal${postedJournals === 1 ? '' : 's'}`}
      />
      <KpiTile
        eyebrow="Last push"
        value={formatRelative(lastPushAt)}
        foot={lastPushAt ? undefined : 'No journals pushed yet'}
        accent={lastPushAt !== null && lastPushHours > 24 ? 'amber' : 'neutral'}
      />
    </div>
  );
}
